import apiV2 from '../api/v2';
import type { CalendarAppointment } from '@odo/shared-ui';

/** The timeline event that prompted an appointment, as the list returns it (no payload). */
export interface AppointmentOriginEventSummary {
    id: string;
    type: string;
}

/** The full origin event (e.g. an in-office-hold note); mirrors the backend's OriginEventResponse. */
export interface AppointmentOriginEvent extends AppointmentOriginEventSummary {
    payload: Record<string, unknown>;
}

/** A row of the cross-practice /admin/appointments list. */
export interface AdminAppointment extends CalendarAppointment {
    originEvent: AppointmentOriginEventSummary | null;
}

/** One appointment from /admin/appointments/:id, origin event payload included. */
export interface AdminAppointmentDetail extends CalendarAppointment {
    originEvent: AppointmentOriginEvent | null;
}

export interface FetchAdminAppointmentsParams {
    from: string;
    to: string;
    includeCanceled?: boolean;
}

/** GET /api/v2/admin/appointments — view-only, cross-practice calendar feed
 *  for the files-viewer admin app. `practiceId` is intentionally omitted:
 *  practice filtering happens client-side against this one bounded fetch. */
export async function fetchAdminAppointments(
    params: FetchAdminAppointmentsParams
): Promise<AdminAppointment[]> {
    // includeProfilePictures is intentionally omitted: this all-practices fan-out is
    // already expensive, and initials avatars are an acceptable fallback here.
    const response = await apiV2.get<{ success: boolean; data: AdminAppointment[] }>(
        '/admin/appointments',
        {
            params: {
                from: params.from,
                to: params.to,
                ...(params.includeCanceled !== undefined && {
                    includeCanceled: params.includeCanceled
                })
            }
        }
    );
    return response.data.data;
}

export async function fetchAdminAppointment(
    appointmentId: string
): Promise<AdminAppointmentDetail> {
    const response = await apiV2.get<{ success: boolean; data: AdminAppointmentDetail }>(
        `/admin/appointments/${encodeURIComponent(appointmentId)}`
    );
    return response.data.data;
}

export interface FetchAdminAppointmentsCountParams {
    from: string;
    to?: string;
    includeCanceled?: boolean;
}

/** GET /api/v2/admin/appointments/count — cheap COUNT query backing the
 *  Work Queue toolbar's upcoming-appointments badge. */
export async function fetchAdminAppointmentsCount(
    params: FetchAdminAppointmentsCountParams
): Promise<number> {
    const response = await apiV2.get<{ success: boolean; data: { count: number } }>(
        '/admin/appointments/count',
        {
            params: {
                from: params.from,
                ...(params.to !== undefined && { to: params.to }),
                ...(params.includeCanceled !== undefined && {
                    includeCanceled: params.includeCanceled
                })
            }
        }
    );
    return response.data.data.count;
}
